"use client";

import type { ReactNode } from "react";

import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { EASE_IN, EASE_MOVE, EASE_OUT, ease, type Beat } from "./motion";

/** Ce que l'anneau de focus déborde : deux pixels de trait, deux de retrait. */
const RING_PX = 4;

/**
 * Un bloc de la fiche qui s'efface puis rend sa place, sans qu'on la mesure.
 *
 * ## Une piste de grille, et non une hauteur
 *
 * `grid-template-rows` passe de `1fr` à `0fr`. La boîte n'a donc jamais de
 * hauteur écrite : elle vaut ce que son contenu vaut, et l'animer ne demande ni
 * `getBoundingClientRect`, ni `FLIP`, ni hauteur en dur qui mentirait au
 * premier titre plus long. Ce qui remonte au-dessus remonte du montant exact,
 * quel que soit le cadre que le studio laisse.
 *
 * `overflow: hidden` sur l'enfant n'est pas décoratif : sans lui, le texte
 * déborde de la piste pendant qu'elle se ferme et passe par-dessus son voisin
 * pendant un cinquième de seconde.
 *
 * ## Le rembourrage est dedans, et c'est une contrainte
 *
 * La scène espaçait ses blocs avec un `gap`. Un `gap` ne se rétracte pas avec
 * son enfant : cinq blocs repliés laissaient cent soixante pixels d'air
 * fantôme, et la titraille ne remontait que d'une fraction de ce qu'elle
 * devait. L'écart appartient donc à la boîte, dans son enfant, où il disparaît
 * avec lui.
 *
 * ## Elle ne connaît pas la phase, elle reçoit ses temps
 *
 * L'aller est une cérémonie et le retour un repli, mais tous les blocs ne
 * partent pas dans le même sens : les cinq de la fiche s'en vont quand le choix
 * s'ouvre, celui du choix s'en va quand la fiche revient. Une boîte qui
 * choisirait son temps d'après son propre `gone` prendrait donc la cadence de
 * l'aller pour un retour. C'est la fiche qui sait dans quel sens on va, et elle
 * passe les temps déjà résolus.
 */
export function Collapsible({
  gone,
  shift,
  fade,
  pad,
  lift = -6,
  stretch = false,
  children,
}: {
  /** Le bloc n'a plus sa place : il s'efface et se rétracte. */
  readonly gone: boolean;
  /** Le grand mouvement, celui que tout ce qui se déplace partage. */
  readonly shift: Beat;
  /**
   * Quand il s'efface, ou `null` quand il tient lui-même son extinction.
   *
   * `null` sert au bloc qui a plusieurs parties à échelonner : la boîte ne fait
   * alors que la place, et rien d'autre.
   */
  readonly fade: Beat | null;
  /** L'écart au-dessus de lui, qui s'en va avec lui. */
  readonly pad: number;
  /**
   * De combien il glisse en s'effaçant, en pixels.
   *
   * Négatif, il monte avec la vague. Positif, il s'enfonce : c'est le cas du
   * seul bloc situé sous le point de clic.
   */
  readonly lift?: number;
  /**
   * La boîte prend toute la largeur de la scène.
   *
   * La scène centre ses blocs, donc chacun se réduit à la largeur de son
   * contenu : c'est ce qu'il faut à une titraille ou à quatre respirations, et
   * c'est mortel pour un bloc qui se borne lui-même en pourcentage — il vaut
   * alors cent pour cent de zéro, et son texte se replie à une lettre par
   * ligne.
   */
  readonly stretch?: boolean;
  readonly children: ReactNode;
}) {
  const reduced = useReducedMotion();
  const curve = gone ? EASE_IN : EASE_OUT;

  return (
    <div
      // `inert` et non `aria-hidden` seul : pendant les six dixièmes de seconde
      // que dure la rétraction, la tabulation visiterait des gestes en train de
      // s'éteindre, et un clic y aboutirait encore.
      inert={gone}
      style={{
        display: "grid",
        alignSelf: stretch ? "stretch" : undefined,
        gridTemplateRows: gone ? "0fr" : "1fr",
        transition: reduced ? "none" : ease("grid-template-rows", shift, EASE_MOVE),
      }}
    >
      {/*
        Deux boîtes et non une : celle qui coupe n'a pas le droit d'avoir de
        rembourrage.

        Le rembourrage appartient à la boîte intérieure, celle qui **est
        coupée**. Posé sur la boîte qui coupe, il survit à la rétraction : une
        boîte ne descend jamais sous la hauteur de son propre rembourrage,
        quelle que soit la piste de grille qui la porte. Trois blocs repliés
        laissaient ainsi quatre-vingt-douze pixels d'air, exactement la somme de
        leurs écarts, et la titraille ne remontait que de la moitié de ce
        qu'elle devait.

        Les quatre pixels sur les trois autres côtés sont la place de l'anneau
        de focus : le navigateur le dessine **en dehors** de ce qu'il entoure,
        deux pixels de trait et deux de retrait, et une boîte qui coupe le
        coupait. Le geste au clavier apparaissait alors amputé de son bord bas.
      */}
      <div style={{ minHeight: 0, overflow: "hidden" }}>
        <div
          style={{
            padding: `${pad}px ${RING_PX}px ${RING_PX}px`,
            opacity: fade === null || !gone ? 1 : 0,
            transform: fade !== null && gone && !reduced ? `translateY(${lift}px)` : "none",
            transition:
              reduced || fade === null
                ? "none"
                : [ease("opacity", fade, curve), ease("transform", fade, curve)].join(", "),
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
